import { useState, useEffect } from 'react';
import { Box, Flex, IconButton, Button, Select } from '@chakra-ui/react';
import { MdDelete, MdSearch, MdEdit } from 'react-icons/md';

import Page from '../components/common/Page';
import { ApiCommand } from '../lib/Api';
import useToastNotification from '../lib/hooks/useToastNotification';
import StrongTable from '../components/common/StrongTable';
import StrongPagination from '../components/common/StrongPagination';
import useApi from '../lib/hooks/useApi';
import urlConstants from '../lib/constants/url.constants';
import StrongInput from '../components/common/StrongInput';
import EditCategoryModal from '../components/categories/EditCategoryModal';
import ThumbnailPreview from '../components/common/ThumbnailPreview';

const CATEGORY_COLUMNS = [
  {
    key: 'title',
    label: 'Title',
  },
  {
    key: 'thumbnail',
    label: 'Thumbnail',
    render: (value: string) => <ThumbnailPreview imageUrl={value} />,
  },
];

const {
  createCategory: createCategoryUrl,
  getCategories: getCategoriesUrl,
  editCategory: editCategoryUrl,
  deleteCategory: deleteCategoryUrl,
} = urlConstants.categories;

const Categories = () => {
  const [categories, setCategories] = useState<Category[]>([]);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [totalCount, setTotalCount] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);
  const [isOpenModal, setIsOpenModal] = useState<boolean>(false);
  const [selectedCategory, setSelectedCategory] = useState<Category>();
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [sortCriteria, setSortCriteria] = useState<string>('NewestAdded');
  const showToast = useToastNotification();

  const { loading: isGetCategoriesLoading, sendRequest: getCategories } =
    useApi<GetCategoriesResopnse>();
  const { loading: isCreateCategoryLoading, sendRequest: createCategory } =
    useApi<any>();
  const { loading: isEditCategoryLoading, sendRequest: editCategory } =
    useApi<any>();
  const { loading: isDeleteCategoryLoading, sendRequest: deleteCategory } =
    useApi<any>();

  const fetchCategories = () => {
    getCategories({
      callback: (data: GetCategoriesResopnse | null, error: string | null) => {
        if (error) {
          showToast({
            title: 'Failed',
            description: error,
            status: 'error',
          });
          return;
        }
        if (!data) return null;
        setCategories(data.categories);
        setTotalPages(data.pagination.totalPages);
        setTotalCount(data.pagination.totalCount);
      },
      command: ApiCommand.GET,
      url: getCategoriesUrl,
      options: {
        page: currentPage,
        limit: pageSize,
        search: searchQuery,
        sortBy: sortCriteria,
      },
    });
  };

  useEffect(() => {
    fetchCategories();
  }, [currentPage, pageSize, searchQuery, sortCriteria]);

  const handlePageSizeChange = (size: number) => {
    setPageSize(size);
    setCurrentPage(1);
  };

  const onSaveCategory = (title: string, thumbnail: File | null) => {
    const formData = new FormData();
    formData.append('title', title);
    if (thumbnail) {
      formData.append('thumbnail', thumbnail);
    }
    if (selectedCategory) {
      formData.append('categoryId', selectedCategory?.id || '');
      editCategory({
        callback: (_data, error: string | null) => {
          if (error) {
            showToast({
              title: 'Failed',
              description: error,
              status: 'error',
            });
            return;
          }
          showToast({
            title: 'Success',
            description: 'Category updated successfully',
            status: 'success',
          });
          setIsOpenModal(false);
          fetchCategories();
        },
        command: ApiCommand.PUT,
        url: editCategoryUrl,
        options: formData,
      });
    } else {
      createCategory({
        callback: (_data, error: string | null) => {
          if (error) {
            showToast({
              title: 'Failed',
              description: error,
              status: 'error',
            });
            return;
          }
          showToast({
            title: 'Success',
            description: 'Category created successfully',
            status: 'success',
          });
          setIsOpenModal(false);
          fetchCategories();
        },
        command: ApiCommand.POST,
        url: createCategoryUrl,
        options: formData,
      });
    }
  };

  const onDeleteCategory = (categoryId: string) => {
    deleteCategory({
      callback: (_data, error: string | null) => {
        if (error) {
          showToast({
            title: 'Failed',
            description: error,
            status: 'error',
          });
          return;
        }
        showToast({
          title: 'Success',
          description: 'Category deleted successfully',
          status: 'success',
        });
        fetchCategories();
      },
      command: ApiCommand.DELETE,
      url: deleteCategoryUrl,
      options: {
        categoryId,
      },
    });
  };

  const onEditCategory = (categoryId: string) => {
    setSelectedCategory(categories.find(({ id }) => id === categoryId));
    setIsOpenModal(true);
  };

  return (
    <Page>
      <Flex flexDirection='column' height='full' justifyContent='space-between'>
        <Box
          backgroundColor='white'
          borderRadius='lg'
          padding={2}
        >
          <Flex justifyContent='space-between' marginBottom={4}>
            <Box flex='1' maxW='400px'>
              <StrongInput
                placeholder='Search'
                rightIcon={<MdSearch color='gray.500' />}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </Box>
            <Select
              value={sortCriteria}
              onChange={(e) => setSortCriteria(e.target.value)}
              width='20%'
            >
              <option value='NameAtoZ'>A-Z</option>
              <option value='NameZtoA'>Z-A</option>
              <option value='NewestAdded'>Newest</option>
              <option value='OldestAdded'>Oldest</option>
            </Select>
            <Button
              colorScheme='primary'
              px={6}
              py={4}
              borderRadius='md'
              fontWeight='bold'
              _hover={{ bg: 'primary.300' }}
              onClick={() => {
                setSelectedCategory(undefined);
                setIsOpenModal(true);
              }}
            >
              Create Category
            </Button>
          </Flex>
          <StrongTable
            columns={CATEGORY_COLUMNS}
            data={categories}
            loading={isGetCategoriesLoading}
            actions={(row) => (
              <>
                <IconButton
                  aria-label='Edit Category'
                  icon={<MdEdit />}
                  variant='outline'
                  colorScheme='primary'
                  size='sm'
                  marginRight={4}
                  onClick={() => onEditCategory(row.id)}
                />
                <IconButton
                  aria-label='Delete Category'
                  icon={<MdDelete />}
                  variant='outline'
                  colorScheme='red'
                  size='sm'
                  onClick={() => onDeleteCategory(row.id)}
                />
              </>
            )}
          />
        </Box>

        <StrongPagination
          currentPage={currentPage}
          totalPages={totalPages}
          totalCount={totalCount}
          pageSize={pageSize}
          onPageChange={setCurrentPage}
          onPageSizeChange={handlePageSizeChange}
        />
      </Flex>

      <EditCategoryModal
        isOpen={isOpenModal}
        onClose={() => setIsOpenModal(false)}
        onSubmit={onSaveCategory}
        selectedCategory={selectedCategory}
      />
    </Page>
  );
};

export default Categories;
